/**
 * The fixed-step simulation: one call to `step()` is one tick of the match.
 *
 * This file is the orchestrator and the public face of core's simulation. The
 * work itself lives in `./sim/`, one module per concern:
 *
 *   state          the shapes everything shares — SimState, PlayerInput,
 *                  SimEvent, WorldQuery — and the constructors for them
 *   casting        readying, wind-ups, resolving and applying a cast
 *   projectiles    flight, hits, blasts, chains and the riders a hit carries
 *                  (burn, chill, shock, debuffs)
 *   placedEffects  walls, signs and storms
 *   movement       running, spread, stance and walking a body through the world
 *   death          damage, death, Call Dead, Resurrect, Valhalla
 *
 * The import graph runs one way — state, then death, then projectiles, then
 * placed effects, then casting, then this file — so no module can reach back
 * up the chain. Keep it that way: a cycle between ES modules works right up to
 * the day somebody reads a constant at load time, and then fails in a way that
 * looks nothing like an import problem.
 *
 * Everything the rest of the codebase imports from `core/sim.ts` is re-exported
 * below, so nothing outside core had to change when this was split.
 */

import { tickArena } from './arena.ts';
import {
  FATIGUE_FLY_DRAIN_PER_SEC,
  FATIGUE_MAX,
  FATIGUE_REGEN_PER_SEC,
  FATIGUE_RUN_DRAIN_PER_SEC,
  FLY_PP_DRAIN_FRACTION,
  HP_REGEN_MOVING,
  HP_REGEN_STILL,
  PP_REGEN_AT_FULL_LEY,
} from './constants.ts';
import { Rng, rollDice } from './dice.ts';
import { ageWarp } from './inputGuard.ts';
import { computeLey } from './ley.ts';
import {
  checkElimination,
  checkVictory,
  factionsInPlay,
  recomputePoints,
  resolveOnTimer,
  SIDES_TO_PLAY,
} from './match.ts';
import { applyDamage, awardXp, type PlayerState } from './player.ts';
import { XP_AWARDS } from './progression.ts';
import { cycleElement, tickCasting, tryCast } from './sim/casting.ts';
import {
  checkValhallaExit,
  killPlayer,
  tryCallGhost,
  tryResurrect,
  trySelfResurrect,
} from './sim/death.ts';
import {
  computeMoveScale, intendCrouch, intendFlight, movePlayer, resolveRunning, spreadFor, steer,
} from './sim/movement.ts';
import { tickBarriers, tickSigns, tickStorms } from './sim/placedEffects.ts';
import { tickProjectiles } from './sim/projectiles.ts';
import {
  emptyInput,
  findPlayer,
  type PlayerInput,
  rosterOf,
  type SimEvent,
  type SimState,
  type WorldQuery,
} from './sim/state.ts';

// ─────────────────────────────────────────────────────────────────────────────
//  RESOURCES
// ─────────────────────────────────────────────────────────────────────────────

function tickResources(p: PlayerState, input: PlayerInput, dt: number): void {
  const moving = Math.abs(input.moveForward) > 0.01 || Math.abs(input.moveRight) > 0.01;
  const running = p.running;

  if (p.flying) {
    p.fatigue = Math.max(0, p.fatigue - FATIGUE_FLY_DRAIN_PER_SEC * dt);
    if (p.fatigue === 0) p.flying = false;
  } else if (running) {
    p.fatigue = Math.max(0, p.fatigue - FATIGUE_RUN_DRAIN_PER_SEC * dt);
  } else {
    p.fatigue = Math.min(FATIGUE_MAX, p.fatigue + FATIGUE_REGEN_PER_SEC * dt);
  }

  // Hit points come back slowly on the move, faster standing still.
  if (p.hp < p.hpMax && !p.effects.some((e) => e.dps || e.tickDamage || e.tickDice)) {
    p.hpAccumulator += (moving ? HP_REGEN_MOVING : HP_REGEN_STILL) * dt;
    if (p.hpAccumulator >= 1) {
      const whole = Math.floor(p.hpAccumulator);
      p.hpAccumulator -= whole;
      p.hp = Math.min(p.hpMax, p.hp + whole);
    }
  }

  for (let i = 0; i < p.cooldowns.length; i++) {
    if (p.cooldowns[i] > 0) p.cooldowns[i] = Math.max(0, p.cooldowns[i] - dt);
  }
}

/**
 * Net power change for one tick, positive or negative — regeneration less
 * whatever flight is costing.
 *
 * The fractional buffer matters in both directions: a single tick at 60Hz almost
 * always moves well under one whole point, and truncating each tick would
 * regenerate nothing at all and, now, drain nothing at all either.
 */
function tickPower(p: PlayerState, perSecond: number, dt: number): void {
  if (perSecond === 0) return;
  // Full and still gaining: nothing to bank, and a stale buffer would leak a
  // point into the next drain.
  if (perSecond > 0 && p.pp >= p.ppMax) {
    p.ppAccumulator = 0;
    return;
  }
  p.ppAccumulator += perSecond * dt;
  while (p.ppAccumulator >= 1) {
    p.ppAccumulator -= 1;
    p.pp = Math.min(p.ppMax, p.pp + 1);
  }
  while (p.ppAccumulator <= -1) {
    p.ppAccumulator += 1;
    p.pp = Math.max(0, p.pp - 1);
  }
}

/**
 * Runs everything currently on a player, and stops the moment one of them kills
 * them.
 *
 * Returning on death is not tidiness. `killPlayer` empties `p.effects`, and this
 * loop is walking that array — so a burn that finished somebody carrying two
 * effects left the next iteration reading an index that no longer existed and
 * threw, taking the whole frame loop with it. Worse, the inner `while` would
 * have carried on burning a corpse first.
 *
 * A dead player has no effects by definition, so there is nothing left to tick.
 */
function tickEffects(p: PlayerState, rng: Rng, dt: number, out: SimEvent[]): void {
  for (let i = p.effects.length - 1; i >= 0; i--) {
    const e = p.effects[i];

    if (e.tickEvery && (e.tickDamage || e.tickDice)) {
      e.nextTick = (e.nextTick ?? e.tickEvery) - dt;
      while (e.nextTick <= 0) {
        e.nextTick += e.tickEvery;
        const amount = e.tickDice ? rollDice(rng, e.tickDice) : (e.tickDamage ?? 0);
        const bit = applyDamage(p, amount, e.element ?? 'physical');
        /*
         * Announced, which it never used to be.
         *
         * Burning, bleeding and Pain all took hit points quietly: the bar moved
         * and nothing said why. The damage numbers even carried a comment about
         * a tier V bleed being eight ticks, describing behaviour that could not
         * happen because no event ever reached them.
         */
        out.push({
          type: 'hit',
          targetId: p.id,
          sourceId: e.sourceId ?? '',
          amount: bit.applied,
          element: e.element ?? 'physical',
          overTime: true,
        });
        if (p.hp === 0 && p.alive) {
          killPlayer(p, e.sourceId ?? null, out);
          return;
        }
      }
    }

    if (e.dps) {
      p.hpAccumulator -= e.dps * dt;
      while (p.hpAccumulator <= -1) {
        p.hpAccumulator += 1;
        p.hp = Math.max(0, p.hp - 1);
      }
      if (p.hp === 0 && p.alive) {
        killPlayer(p, e.sourceId ?? null, out);
        return;
      }
    }

    e.remaining -= dt;
    if (e.remaining <= 0) p.effects.splice(i, 1);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN STEP
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One fixed simulation step. Movement integration lives in the render layer for
 * now, since Rapier owns collision; everything that decides who wins lives here
 * so the server can run the identical function.
 */
export function step(
  s: SimState,
  inputs: Map<string, PlayerInput>,
  world: WorldQuery,
  dt: number,
): SimEvent[] {
  const out: SimEvent[] = [];
  /*
   * Refreshed every tick, before anything can return early.
   *
   * Before the phase gates, because a scoreboard has to be right during a freeze
   * and an intermission too — those are exactly the moments somebody looks at
   * it.
   */
  s.roster = rosterOf(s.players);
  s.tick += 1;

  /*
   * A freeze before the round, not a phase that lasts one tick.
   *
   * `warmup` used to flip to `live` immediately, which made it a label rather
   * than a state — everybody was fighting before the map had finished appearing
   * on the slowest machine in the room. Ten seconds of everybody spawned and
   * held still is what every round-based shooter does with the moment before a
   * round, and it is the only point at which a player can look at where they are
   * without being shot for it.
   *
   * Nothing simulates during it. That is the freeze: no movement, no casting, no
   * clock. The pause is the point.
   */
  if (s.match.phase === 'warmup') {
    /*
     * The countdown waits for somebody to fight.
     *
     * A round with one side on the board is not a round, and starting it anyway
     * used to end it on the same tick — see `checkVictory`. Holding here instead
     * is what every shooter does with a half-empty server, and it is the state a
     * host is in for the minute between opening a lobby and a friend arriving.
     *
     * Held at full rather than paused part-way: the ten seconds are for getting
     * your bearings, and somebody who joins deserves all of them rather than
     * whatever was left when the room gave up waiting.
     */
    const waiting = factionsInPlay(s.players).length < SIDES_TO_PLAY;
    /*
     * Held where it is, and only if there was a freeze to hold.
     *
     * The first version raised the clock back to `WARMUP_SECONDS` whenever a
     * side was missing, which is right for a room that wanted a freeze and wrong
     * for one that asked for none — a state built with `warmupSeconds: 0` means
     * "start when stepped", and seven core tests with a single faction on the
     * board found themselves frozen for ever in a countdown they had explicitly
     * declined.
     *
     * Holding rather than resetting also does the right thing for the only case
     * where the clock is part-way down with a side missing: somebody left during
     * the countdown, and it resumes rather than starting over.
     */
    if (waiting && s.warmup > 0) return out;
    s.warmup -= dt;
    if (s.warmup > 0) return out;
    s.warmup = 0;
    s.match.phase = 'live';
  }
  /*
   * A finished round simulates nothing, and neither does the scoreboard after
   * it. `intermission` is checked here rather than being treated as a kind of
   * `over` because the two mean different things to everything upstream: `over`
   * is a result waiting to be read, `intermission` is a countdown to the next
   * round, and the room advances one to the other.
   */
  /*
   * The lobby simulates nothing, like the phases either side of a round.
   *
   * Counted down by the *room* rather than here, because whether it should be
   * counting at all depends on who is ready — which is a fact about the room's
   * members rather than about the arena.
   */
  if (s.match.phase === 'lobby') return out;
  if (s.match.phase === 'over' || s.match.phase === 'intermission') return out;

  s.match.timeRemaining -= dt;

  for (const p of s.players) {
    const input = inputs.get(p.id) ?? emptyInput();
    steer(p, input);
    if (input.selectSlot >= 0 && input.selectSlot < p.loadout.length) {
      p.selectedSlot = input.selectSlot;
    }
    cycleElement(p, input);
    p.gcd = Math.max(0, p.gcd - dt);
    p.raiseCooldown = Math.max(0, p.raiseCooldown - dt);
    p.callCooldown = Math.max(0, p.callCooldown - dt);

    // Above the ghost branch, because a ghost drifting toward a Cleric has to be
    // moved too — and below it, the branch has already jumped to the next player.
    movePlayer(p, input, world, dt);

    if (p.ghost) {
      if (input.selfResurrect) trySelfResurrect(s, p, out);
      continue;
    }

    // Serving time is a living state, so this sits outside the ghost branch.
    if (p.inValhalla) checkValhallaExit(s, p, out);

    // The grace after a legitimate teleport, counted down. See `inputGuard`.
    ageWarp(p, dt);
    tickEffects(p, s.rng, dt, out);
    if (!p.alive) continue;

    // Running is settled before anything reads it, so the renderer's speed, the
    // fatigue drain and the reticle all agree about it within a tick.
    resolveRunning(p, input);

    // Flight intent resolves before resources, because fatigue drain reads it.
    intendFlight(p, input);

    tickResources(p, input, dt);

    p.ley = p.inValhalla ? 0 : computeLeyFor(p, s, dt);
    if (p.inValhalla) {
      // Both bars sit visibly empty. Zeroing power is not what stops you casting
      // — the guard in tryCast does that — but a full blue bar you cannot spend
      // reads as a broken button rather than a sealed room.
      p.pp = 0;
      p.ppAccumulator = 0;
    }
    const regen = p.ppMax * p.ley * PP_REGEN_AT_FULL_LEY;
    const flightCost = p.flying ? p.ppMax * FLY_PP_DRAIN_FRACTION : 0;
    tickPower(p, regen - flightCost, dt);
    // Out of power is out of altitude, the same way out of fatigue already was —
    // and it latches, or a held key puts them back up on the single point that
    // regenerates before the next tick.
    if (p.flying && p.pp === 0) {
      p.flying = false;
      p.drained = true;
    }

    intendCrouch(p, input);
    p.spread = spreadFor(p, input);

    p.moveScale = computeMoveScale(s, p);

    if (input.callGhost) tryCallGhost(s, p, out);
    if (input.resurrect) tryResurrect(s, p, out);
    tickCasting(s, p, world, dt, out);
    if (input.fire) tryCast(s, p, world, out);
  }

  tickProjectiles(s, world, dt, out);
  tickStorms(s, dt, out);
  tickBarriers(s, dt, out);
  tickSigns(s, dt, out);

  for (const ev of tickArena(s.arena, s.players, dt)) {
    if (ev.type === 'pool-biased') {
      s.match.scores[ev.faction].poolsBiased += 1;
      out.push({ type: 'pool', poolId: ev.id, faction: ev.faction });
      for (const id of ev.credit) {
        const p = findPlayer(s, id);
        if (p) {
          p.poolsBiased += 1;
          if (awardXp(p, XP_AWARDS.poolBiased * s.xpScale)) out.push({ type: 'level', playerId: p.id, level: p.level });
        }
      }
    } else if (ev.type === 'shrine-conquered') {
      s.match.scores[ev.faction].shrinesConquered += 1;
      out.push({ type: 'shrine', shrineId: ev.id, faction: ev.faction, conquered: true });
      for (const id of ev.credit) {
        const p = findPlayer(s, id);
        if (p) {
          p.shrinesConquered += 1;
          if (awardXp(p, XP_AWARDS.shrineConquered * s.xpScale)) out.push({ type: 'level', playerId: p.id, level: p.level });
        }
      }
      if (ev.from && ev.from !== 'neutral') {
        if (checkElimination(s.match, s.arena, s.players, ev.from)) {
          out.push({ type: 'eliminated', faction: ev.from });
        }
      }
    } else if (ev.type === 'shrine-restored') {
      out.push({ type: 'shrine', shrineId: ev.id, faction: ev.faction, conquered: false });
    }
  }

  // Death may have emptied a faction whose shrine is already down.
  for (const ev of out.filter((e) => e.type === 'death')) {
    const dead = findPlayer(s, (ev as { playerId: string }).playerId);
    if (dead && checkElimination(s.match, s.arena, s.players, dead.faction)) {
      out.push({ type: 'eliminated', faction: dead.faction });
    }
  }

  if (checkVictory(s.match, s.players)) {
    recomputePoints(s.match);
    out.push({ type: 'victory', faction: s.match.winner, reason: 'elimination' });
  } else if (s.match.timeRemaining <= 0) {
    resolveOnTimer(s.match, s.players);
    out.push({ type: 'victory', faction: s.match.winner, reason: 'timer' });
  }

  return out;
}
function computeLeyFor(p: PlayerState, s: SimState, dt: number): number {
  return computeLey(p, s.arena, dt);
}

// ── The public surface, unchanged by the split ──────────────────────────────

export {
  emptyInput, eyePos, findPlayer, makeSimState, rosterOf,
  type BodyMove, type PlayerInput, type Projectile, type RayHit, type RosterEntry,
  type SimEvent, type SimState, type WorldQuery,
} from './sim/state.ts';
export { movePlayer, predictTick } from './sim/movement.ts';
export { applyChill, volleyPattern } from './sim/projectiles.ts';
export { makePlayer } from './player.ts';
